import React, { useEffect, useRef, useState } from 'react';
import { TermItem } from './TermItem';

export const SearchFilters = ({ listing, filters, selectedFilters, dateParams, facets, onFilterChange }) => {
  // Convert ISO dates to dd/mm/yyyy format
  const convertISOToDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      console.error('Error converting date:', e);
      return '';
    }
  };

  // Initialize resetFilters with both existing filters and converted dates
  const resetFilters = {
    ...selectedFilters
  };

  // Add date parameters if they exist
  if (dateParams?.date_from) {
    resetFilters.date_from = convertISOToDisplayDate(dateParams.date_from);
  }
  if (dateParams?.date_to) {
    resetFilters.date_to = convertISOToDisplayDate(dateParams.date_to);
  }

  ['from', 'to'].forEach(key => {
    const dateKey = `date_${key}`;
    const date = listing[dateKey];
    if (date) {
      // Convert from ISO to dd/mm/yyyy
      const isoDate = new Date(date);
      const day = String(isoDate.getUTCDate()).padStart(2, '0');
      const month = String(isoDate.getUTCMonth() + 1).padStart(2, '0');
      const year = isoDate.getUTCFullYear();
      resetFilters[dateKey] = `${day}/${month}/${year}`;
    }
  });

  const [preparedFilters, setPreparedFilters] = useState({
    ...selectedFilters,
    ...resetFilters,
    tags: selectedFilters.tags || { groups: {}, ungrouped: [] }
  })
  const [hasInteracted, setHasInteracted] = useState(false)
  const ref = useRef()

  // Update prepared filters when date params change
  useEffect(() => {
    if (dateParams) {
      setPreparedFilters(prev => ({
        ...prev,
        date_from: dateParams.date_from ? convertISOToDisplayDate(dateParams.date_from) : undefined,
        date_to: dateParams.date_to ? convertISOToDisplayDate(dateParams.date_to) : undefined
      }));
    }
  }, [dateParams]);

  useEffect(() => {
    ref.current.querySelectorAll('.js-date-input__text').forEach(input => {
      const value = preparedFilters[input.name];
      if (value !== undefined) {
        input.value = value;
      } else {
        input.value = '';
      }
    });
  }, [preparedFilters]);

  useEffect(() => {
    ref.current.querySelectorAll('.js-date-input__text').forEach(input => {
      input.addEventListener('change', () => {
        setHasInteracted(true);
        setPreparedFilters(prev => {
          const newFilters = {...prev};
          const value = input.value;
          if (value) {
            newFilters[input.name] = value;
          } else {
            delete newFilters[input.name];
          }
          return newFilters;
        });
      })
    })
  }, [])
  useEffect(() => {
    ref.current.querySelectorAll('.js-date-input__text').forEach(input => {
      if (preparedFilters[input.name] === undefined) {
        input.value = ''
      }
    })
  }, [preparedFilters])

  // Get root level terms (terms with no ancestors or empty ancestors array)
  const getRootTerms = (terms) => {
    return terms.filter(term => !term.ancestors || term.ancestors.length === 0);
  };

  const updatePreparedFilters = (filterKey, termIds, isChecked, groupId = null) => {
    setHasInteracted(true);
    setPreparedFilters(prev => {
      const newFilters = {...prev};
  
      if (filterKey === 'tags') {
        if (!newFilters[filterKey]) {
          newFilters[filterKey] = {
            groups: {},
            ungrouped: []
          };
        }
        
        if (groupId) {
          // Handle grouped terms (OR within group)
          if (!newFilters[filterKey].groups[groupId]) {
            newFilters[filterKey].groups[groupId] = [];
          }
          if (isChecked) {
            newFilters[filterKey].groups[groupId] = [
              ...new Set([...newFilters[filterKey].groups[groupId], ...termIds])
            ];
          } else {
            newFilters[filterKey].groups[groupId] = newFilters[filterKey].groups[groupId]
              .filter(id => !termIds.includes(id));
          }
        } else {
          // Handle ungrouped terms (AND with other groups)
          if (isChecked) {
            newFilters[filterKey].ungrouped = [
              ...new Set([...newFilters[filterKey].ungrouped, ...termIds])
            ];
          } else {
            newFilters[filterKey].ungrouped = newFilters[filterKey].ungrouped
              .filter(id => !termIds.includes(id));
          }
        }
      } else {
        // Other vocabularies remain unchanged
        if (!newFilters[filterKey]) {
          newFilters[filterKey] = [];
        }
        if (isChecked) {
          newFilters[filterKey] = [...new Set([...newFilters[filterKey], ...termIds])];
        } else {
          newFilters[filterKey] = newFilters[filterKey].filter(id => !termIds.includes(id));
        }
      }
  
      return newFilters;
    });
  };

  const getAllTermIdsInBranch = (terms, rootTermIds) => {
    let allIds = [];

    // Add each root term
    rootTermIds.forEach(rootId => {
      const rootTerm = terms.find(term => term.tid === rootId);
      if (rootTerm) {
        // Add the root term ID
        allIds.push(rootTerm.tid);

        // Recursively add all children
        if (rootTerm.children && rootTerm.children.length > 0) {
          rootTerm.children.forEach(childId => {
            const childTermIds = getAllTermIdsInBranch(terms, [childId.toString()]);
            allIds = [...allIds, ...childTermIds];
          });
        }
      }
    });

    return allIds;
  };

  const formatDate = date => {
    if (!date) {
      return ''
    }

    const parts = date.split('T')[0].split('-')
    return parts[1]+'/'+parts[2]+'/'+parts[0]
  }
  return (
    <div id="sidebarFormDynamicSearch" ref={ref}>
      {['from', 'to'].map(key => (
        <div
          key={key}
          className="form-item"
          data-min-date={formatDate(listing.date_from)}
          data-max-date={formatDate(listing.date_to)}
        >
          <legend>
            <label htmlFor={`date-published-${key}`} className="nsw-form__label">
              Date published {key}
            </label>
          </legend>

          <input
            type="text"
            className="nsw-form__input js-date-input__text"
            autoComplete="off"
            name={`date_${key}`}
            id={`date-published-${key}`}
            placeholder="dd/mm/yyyy"
            defaultValue={preparedFilters[`date_${key}`] || ''}
          />
        </div>
      ))}
      {Object.entries(filters).map(([key, filter]) => {
        const details = (toggleName, rootTerms, identifier = '') => {
          if (!rootTerms.length) {
            return
          }
          const anySelected = key === 'tags' 
          ? preparedFilters[key] && (
              // Check both groups and ungrouped for tags
              rootTerms.some(term => 
                preparedFilters[key].ungrouped?.includes(term.tid) ||
                Object.values(preparedFilters[key].groups || {}).some(group => 
                  group.includes(term.tid)
                )
              )
            )
          : preparedFilters[key] && rootTerms.some(term => 
              preparedFilters[key].includes(term.tid)
            );
          return <details key={`${key}-${identifier}`} className="form-item" open>
            <summary>{toggleName}</summary>
            <a
              href="#"
              onClick={e => {
                e.preventDefault();
                const allTermIds = getAllTermIdsInBranch(
                  filter.terms,
                  rootTerms.map(term => term.tid)
                );
                updatePreparedFilters(
                  key, 
                  allTermIds, 
                  !anySelected,
                  key === 'tags' ? toggleName : null
                );
              }}
            >
              Select {anySelected ? 'None' : 'All'}
            </a>
            <div>
              <ul>
                {rootTerms.map((term) => (
                  <TermItem
                    key={term.tid}
                    term={term}
                    allTerms={filter.terms}
                    filter={filter}
                    facets={facets}
                    selectedFilters={preparedFilters}
                    onFilterChange={updatePreparedFilters}
                    filterKey={key}
                    groupId={key === 'tags' ? toggleName : null}
                  />
                ))}
              </ul>
            </div>
          </details>
        }

        let rootTerms = getRootTerms(filter.terms)
        const allRootTermsSelected = rootTerms.every(term =>
          filter.terms.some(t => t.tid === term.tid)
        );

        // If all root terms are selected, use taxonomy name as toggle
        let toggleName = key[0].toUpperCase() + key.slice(1)
        if (!allRootTermsSelected && rootTerms.length === 1) {
          toggleName = rootTerms[0].name
        }

        if (key == 'tags') {
          const extra = []
          // Only show terms at root level if they're marked for root display
          rootTerms = filter.terms.filter(term => {
            if (term.showAtRoot) {
              if (term.children.length) {
                extra.push(term)
                return false
              }
              return true
            }
            return false
          })

          return <React.Fragment key={key}>
            {details(toggleName, rootTerms, 'root')}
            {extra.map(term => details(
              term.name, 
              term.children.map(childId => filter.terms.find(t => t.tid === childId.toString())),
              `term-${term.tid}`
            ))}
          </React.Fragment>
        } else {
          return details(toggleName, rootTerms, 'root')
        }
      })}
      <div className="nsw-m-y-md">
        <button
          className="nsw-button nsw-button--dark"
          onClick={() => onFilterChange(preparedFilters)}
          disabled={!hasInteracted}
        >
          Apply filters
        </button>
        {(Object.keys(preparedFilters).length !== 0 || Object.keys(dateParams).length !== 0) ? (
          <a
            href="#"
            className="nsw-m-left-sm"
            onClick={e => {
              e.preventDefault();
              setPreparedFilters({
                tags: { groups: {}, ungrouped: [] }
              });
              setHasInteracted(false);
              // Clear the date input fields
              ref.current.querySelectorAll('.js-date-input__text').forEach(input => {
                input.value = '';
              });
              onFilterChange({ tags: { groups: {}, ungrouped: [] } }, 'clear');
            }}
          >
            Clear all filters
          </a>
        ) : undefined}
      </div>
    </div>
  );
};
